import React, { useEffect, useState } from "react";
import { MapPin, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { INDIA_STATES, matchState } from "@/lib/india";
import { useStore } from "@/context/StoreContext";
import { useCustomer } from "@/context/CustomerContext";
import { personErrors } from "@/lib/personValidation";
import { Button, Card, Field, Modal, inputCls, inputStyle } from "./AccountUI";

const LABELS = ["Home", "Work", "Other"];
const blank = { label: "Home", address: "", city: "", state: "", pincode: "", is_default: false };

function AddressForm({ s, open, initial, hasAny, onClose, onSaved }) {
  const cu = useCustomer();
  const [f, setF] = useState(blank);
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [pinNote, setPinNote] = useState("");                 // status text under the pincode field
  const pin = f.pincode.trim();
  // Same as checkout: a valid pincode fills City and State (still editable afterwards).
  useEffect(() => {
    if (!open || !/^[1-9]\d{5}$/.test(pin)) { setPinNote(""); return undefined; }
    let live = true;
    setPinNote("Finding your city and state…");
    api.get(`/pincode/${pin}`).then(({ data }) => {
      if (!live) return;
      const state = matchState(data.state);
      setF((x) => (x.pincode.trim() === pin ? { ...x, city: data.city || x.city, state: state || x.state } : x));
      setErrs((e) => ({ ...e, city: data.city ? undefined : e.city, state: state ? undefined : e.state }));
      setPinNote(data.city ? `${data.city}, ${data.state}` : "");
    }).catch((err) => { if (live) setPinNote(err.response?.status === 404 ? "We couldn't find this pincode. Please check it, or fill in the city and state yourself." : ""); });
    return () => { live = false; };
  }, [open, pin]);
  useEffect(() => { if (open) { setF({ ...blank, ...(initial || {}), is_default: initial?.id ? !!initial.is_default : !hasAny }); setErrs({}); } }, [open, initial, hasAny]);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrs((e) => ({ ...e, [k]: undefined })); };
  const save = async () => {
    const all = personErrors({ name: "Valid Name", mobile: "9999999999", ...f }, { delivery: true });
    const e = Object.fromEntries(["address", "city", "state", "pincode"].filter((k) => all[k]).map((k) => [k, all[k]]));
    if (f.label.length > 30) e.label = "At most 30 characters.";
    setErrs(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    const body = { label: f.label.trim(), address: f.address.trim(), city: f.city.trim(), state: f.state.trim(), pincode: f.pincode.trim(), is_default: f.is_default };
    try {
      const { data } = initial?.id ? await cu.api.put(`/addresses/${initial.id}`, body) : await cu.api.post("/addresses", body);
      toast.success(initial?.id ? "Address updated." : "Address added.");
      onSaved(data.addresses); onClose();
    } catch (err) {
      const msg = apiErr(err.response?.data?.detail);
      if (err.response?.status === 409) setErrs({ address: msg }); else toast.error(msg);
    } finally { setSaving(false); }
  };
  const inp = (k, props = {}) => <input value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`acct-addr-${k}`} className={inputCls} style={inputStyle(s, errs[k])} {...props} />;
  return (
    <Modal s={s} open={open} onClose={onClose} title={initial?.id ? "Edit address" : "Add address"} testId="account-address-modal"
      footer={<><Button s={s} variant="ghost" onClick={onClose}>Cancel</Button><Button s={s} onClick={save} disabled={saving} data-testid="acct-addr-save">{saving ? "Saving…" : "Save address"}</Button></>}>
      <div className="space-y-4">
        <Field s={s} label="Address type" error={errs.label}>
          <div role="radiogroup" aria-label="Address type" className="flex gap-2 flex-wrap" data-testid="acct-addr-label">
            {LABELS.map((l) => {
              const on = l === "Other" ? !["Home", "Work"].includes(f.label) : f.label === l;      // an older custom label counts as Other until changed
              return (
                <label key={l} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-semibold cursor-pointer transition-colors"
                  style={on ? { borderColor: s.c.accent, background: `${s.c.accent}14`, color: s.c.text } : { borderColor: s.border, color: s.muted }}>
                  <input type="radio" name="acct-addr-type" value={l} checked={on} onChange={() => set("label", l)} data-testid={`acct-addr-label-${l.toLowerCase()}`} className="accent-current" />
                  {l}
                </label>
              );
            })}
          </div>
        </Field>
        <Field s={s} label="Address" error={errs.address}><textarea rows={2} maxLength={300} value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="acct-addr-address" className={inputCls} style={inputStyle(s, errs.address)} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field s={s} label="Pincode" error={errs.pincode}>
            <input value={f.pincode} maxLength={6} inputMode="numeric" placeholder="6-digit pincode" onChange={(e) => set("pincode", e.target.value.replace(/\D/g, ""))} data-testid="acct-addr-pincode" className={inputCls} style={inputStyle(s, errs.pincode)} />
            {!errs.pincode && pinNote && <span className="block text-[11px] mt-1" style={{ color: s.muted }} data-testid="acct-addr-pincode-note">{pinNote}</span>}
          </Field>
          <Field s={s} label="City" error={errs.city}>{inp("city", { maxLength: 60 })}</Field>
          <Field s={s} label="State" error={errs.state}>
            <select value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="acct-addr-state" className={inputCls} style={inputStyle(s, errs.state)}>
              <option value="">Select state</option>
              {(f.state && !INDIA_STATES.includes(f.state) ? [f.state, ...INDIA_STATES] : INDIA_STATES).map((st) => <option key={st} value={st} style={{ color: "#000" }}>{st}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} disabled={initial?.id && initial.is_default} onChange={(e) => set("is_default", e.target.checked)} /> Make this my default address</label>
      </div>
    </Modal>
  );
}

export default function AccountAddresses() {
  const s = useStore();
  const cu = useCustomer();
  const [addrs, setAddrs] = useState(null);
  const [edit, setEdit] = useState(null);     // null closed, {} new, address = edit
  useEffect(() => { cu.api.get("/addresses").then(({ data }) => setAddrs(data.addresses)).catch((e) => { toast.error(apiErr(e.response?.data?.detail)); setAddrs([]); }); }, [cu.api]);

  const act = async (fn, ok) => { try { const { data } = await fn(); setAddrs(data.addresses); toast.success(ok); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  const remove = (a) => { if (window.confirm(`Delete this address?\n${a.address}, ${a.city}`)) act(() => cu.api.delete(`/addresses/${a.id}`), "Address deleted."); };
  if (!addrs) return <p style={{ color: s.muted }}>Loading your addresses…</p>;

  return (
    <div data-testid="account-addresses">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div><h2 className="text-3xl font-extrabold" style={{ color: s.c.primary }}>My addresses</h2><p className="mt-1" style={{ color: s.muted }}>Save up to 10 delivery addresses.</p></div>
        <Button s={s} onClick={() => setEdit({})} disabled={addrs.length >= 10} data-testid="acct-addr-add"><Plus size={16} /> Add address</Button>
      </div>
      {addrs.length === 0 ? (
        <Card s={s} className="mt-6 p-12 text-center" ><MapPin className="mx-auto mb-3 opacity-50" size={30} style={{ color: s.muted }} /><p style={{ color: s.muted }}>No saved addresses yet.</p></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {addrs.map((a) => (
            <Card key={a.id} s={s} className="p-5" data-testid={`acct-addr-card-${a.id}`} style={a.is_default ? { outline: `2px solid ${s.c.primary}` } : undefined}>
              <div className="flex items-center gap-2 mb-2"><MapPin size={16} style={{ color: s.c.primary }} /><span className="font-bold">{a.label || "Address"}</span>
                {a.is_default && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 text-white" style={{ background: s.c.primary }}><Star size={10} /> Default</span>}</div>
              <p className="text-sm" style={{ color: s.muted }}>{a.address}<br />{a.city}, {a.state} {a.pincode}</p>
              <div className="flex gap-4 mt-4 text-sm font-semibold">
                <button onClick={() => setEdit(a)} style={{ color: s.c.primary }} data-testid={`acct-addr-edit-${a.id}`}>Edit</button>
                {!a.is_default && <button onClick={() => act(() => cu.api.post(`/addresses/${a.id}/default`), "Default address changed.")} data-testid={`acct-addr-default-${a.id}`}>Make default</button>}
                <button onClick={() => remove(a)} className="ml-auto text-red-600 inline-flex items-center gap-1" data-testid={`acct-addr-delete-${a.id}`}><Trash2 size={14} /> Delete</button>
              </div>
            </Card>
          ))}
        </div>
      )}
      <AddressForm s={s} open={!!edit} initial={edit?.id ? edit : null} hasAny={addrs.length > 0} onClose={() => setEdit(null)} onSaved={setAddrs} />
    </div>
  );
}
